// Label dictionaries and asset helpers shared by the B2 trading-config pages,
// ported verbatim from the legacy Angular components
// (`v3-settings-default.component.ts`, `asset-helper.ts`, and the coin
// settings popup — see each constant's comment).

/** `V3SettingsDefaultComponent.offModeTriggers` — verbatim. */
export const OFF_MODE_TRIGGERS: Record<string, string> = {
  '0': 'Do nothing',
  '1': 'Sell Lowest Open Position',
  '2': 'Sell Highest Open Position',
  '3': 'Sell TOP 50% of Open Positions',
  '4': 'Sell BOTTOM 50% of Open Positions',
  '5': 'Sell All Open Positions',
  '6': 'Sell All Open Positions AND Wallet',
  '11': 'Start MirrorTrading on the Lowest Open Position',
  '12': 'Start MirrorTrading on the Highest Open Position',
  '13': 'Start MirrorTrading on the TOP 50% Open Positions',
  '14': 'Start MirrorTrading on the BOTTOM 50% Open Positions',
  '15': 'Start MirrorTrading on All Open Positions',
}
/** The template's three option groups (`[0]`, "Sell Open Positions",
 * "MirrorTrading"). */
export const OFF_MODE_TRIGGER_GROUPS: { label: string | null; values: number[] }[] = [
  { label: null, values: [0] },
  { label: 'Sell Open Positions', values: [1, 2, 3, 4, 5, 6] },
  { label: 'MirrorTrading', values: [11, 12, 13, 14, 15] },
]

/** `V3SettingsDefaultComponent.autoBuyModes` — verbatim. */
export const AUTO_BUY_MODES: Record<string, string> = {
  '0': 'Disabled',
  '1': 'Buy till balance if greater than MOP count',
  '2': "Buy only if last MOP can't be closed",
}

/** `AssetHelper` (`src/app/data/api-client/asset-helper.ts`). */
export const PRIMARY_USD_ASSET = 'USDC'
export const USD_ASSETS = ['USDT', 'USDC']

export function isUsd(asset: string | null | undefined): boolean {
  return USD_ASSETS.includes(String(asset ?? '').trim().toUpperCase())
}

/** Default auto-buy / bid-constant quote asset lists
 * (`V3SettingsDefaultComponent.setExchangeId`), extended with every `coin2`
 * the user's coin configs use. */
export const BASE_AUTO_BUY_QUOTE_ASSETS = ['*', 'USDT', 'USDC', 'BTC', 'ETH', 'BNB']
export const BASE_BID_CONSTANT_QUOTE_ASSETS = ['USDT', 'USDC', 'BTC', 'ETH', 'BNB']

/** `getNameOfBidConstant` / `getAssetOfBidConstant`: a bid constant key is
 * `NAME::ASSET`; no asset part means the primary USD asset. Keys may arrive
 * as JSON numbers (PHP integer-like dictionary keys). */
export function bidConstantName(key: string | number): string {
  return String(key).split('::')[0] ?? ''
}
export function bidConstantAsset(key: string | number): string {
  const parts = String(key).split('::')
  return parts.length < 2 || !parts[1] ? PRIMARY_USD_ASSET : parts[1]
}

/** Legacy `app-datetime-display [isUtc]="true"` — backend timestamps are
 * ISO-8601 with offset; show them in the viewer's local time. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export function toNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : fallback
}

/** `v3-settings-default.component.html`'s hard-coded "Start … BOT" buttons
 * (B4 bot control). `wnfexchange` is additionally gated to
 * `@webbandflow.co.uk` accounts, same as legacy
 * (`user.username.indexOf('@webbandflow.co.uk') > -1`) — checked by the
 * caller, not listed here. */
export const STARTABLE_EXCHANGES: { exchangeId: string; label: string }[] = [
  { exchangeId: 'binance', label: 'Start Binance BOT' },
  { exchangeId: 'bybit', label: 'Start Bybit BOT' },
]
export const WNFEXCHANGE = { exchangeId: 'wnfexchange', label: 'Start WnF Exchange BOT' }
export const WNFEXCHANGE_EMAIL_DOMAIN = '@webbandflow.co.uk'

/** The exchange FAMILY of a connection id: spotbot renames a WnF Exchange
 * connection to `wnfexchange_<ms timestamp>` on create, so the family is the
 * part before the first underscore (`binance`, `bybit`, `wnfexchange`). */
export function exchangeFamily(exchangeId: string): string {
  return exchangeId.split('_')[0] ?? exchangeId
}

/** Display name of an exchange id in user-visible text ("WnF Exchange", never
 * "wnfexchange"/"WnfExchange"; WnF is the company, so never "WnF" alone). */
export function exchangeDisplayName(exchangeId: string): string {
  if (exchangeFamily(exchangeId) === 'wnfexchange') return 'WnF Exchange'
  if (exchangeId === 'binance') return 'Binance'
  if (exchangeId === 'bybit') return 'Bybit'
  return exchangeId
}
